import { FC, useState, type TransitionEvent } from 'react';
import clsx from 'clsx';
import { CloseButton } from '@mantine/core';
import { MdOutlineWarningAmber } from 'react-icons/md';
import Button from '@srm/ui/button';
import { isBrowserOutdated, isUpdateDismissed, dismissUpdate, getBrowserUpdateUrl } from '@srm/ui/lib/browserSupport';
import './BrowserSupportBanner.scss';

/**
 * Баннер «браузер устарел» — soft-слой поддержки браузера.
 *
 * Тот же визуальный язык и поведение, что у {@link ReleaseNotes}: мягко-жёлтая
 * attention-плашка в зоне `.notes`, плавное схлопывание при закрытии. Показывается,
 * только если браузер запускается, но не поддерживает `:has()` ({@link isBrowserOutdated})
 * и пользователь недавно не нажимал «Позже» ({@link isUpdateDismissed}). Браузеры,
 * которые вовсе не запускают приложение, перехватывает ES5-детектор в index.html.
 */
const BrowserSupportBanner: FC = () => {
    // Решение о показе принимаем один раз на mount.
    const [hidden, setHidden] = useState(() => !isBrowserOutdated() || isUpdateDismissed());
    const [isClosing, setIsClosing] = useState(false);

    if (hidden) {
        return null;
    }

    const handleUpdate = () => {
        window.open(getBrowserUpdateUrl(), '_blank', 'noopener,noreferrer');
    };

    // И «Позже», и крестик откладывают напоминание и плавно схлопывают баннер.
    const handleDismiss = () => {
        dismissUpdate();
        setIsClosing(true);
    };

    // Убираем из DOM только после завершения анимации схлопывания (как в ReleaseNotes).
    const handleTransitionEnd = (e: TransitionEvent<HTMLDivElement>) => {
        if (isClosing && e.propertyName === 'grid-template-rows') {
            setHidden(true);
        }
    };

    return (
        <div
            className={clsx('rf-browser-banner', isClosing && 'rf-browser-banner--closing')}
            onTransitionEnd={handleTransitionEnd}
            role="status"
        >
            <div className="rf-browser-banner-inner">
                <div className="rf-browser-banner-bar">
                    <div className="rf-browser-banner-icon" aria-hidden="true">
                        <MdOutlineWarningAmber size="20" />
                    </div>
                    <div className="rf-browser-banner-body">
                        <div className="rf-browser-banner-content">
                            <b>Ваш браузер устарел.</b> Bidzaar SRM продолжит работать, но для стабильной и безопасной
                            работы рекомендуем обновить его до актуальной версии.
                        </div>
                    </div>
                    <div className="rf-browser-banner-actions">
                        <Button variant="primary" size="sm" onClick={handleUpdate}>
                            Обновить
                        </Button>
                        <Button variant="outline-neutral" size="sm" onClick={handleDismiss}>
                            Позже
                        </Button>
                        <CloseButton
                            size={32}
                            iconSize={20}
                            className="rf-browser-banner-close"
                            aria-label="Закрыть"
                            onClick={handleDismiss}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BrowserSupportBanner;
